import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import zeutecConfig from 'zeutecConfig';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const Icon = item.icon;
    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} size="1.3rem" />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
    };

    // active menu item on page load
    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
        // eslint-disable-next-line
    }, []);

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: '25px',
                mb: 0.6,
                alignItems: 'center',
                py: 0.7,
                px: 1.4,
                pl: `${level * 24}px`,
                color: '#1c1c1c',
                transition: 'all 0.2s ease',

                '& .MuiListItemIcon-root': {
                    color: '#1c1c1c',
                    minWidth: 36
                },

                '&:hover': {
                    backgroundColor: '#f9f9fc',
                    color: '#c80645',

                    '& .MuiListItemIcon-root': {
                        color: '#c80645'
                    }
                },

                '&.Mui-selected': {
                    backgroundColor: '#c80645',
                    color: '#f9f9f9',

                    '& .MuiListItemIcon-root': {
                        color: '#f9f9f9'
                    },

                    '&:hover': {
                        backgroundColor: '#c80645'
                    }
                }
            }}
            selected={customization.isOpen.findIndex((id) => id === item.id) > -1}
            onClick={() => itemHandler(item.id)}
        >
            <ListItemIcon
                sx={{
                    my: 'auto',
                    minWidth: 36,
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                {itemIcon}
            </ListItemIcon>
            <ListItemText
                primary={
                    <Typography
                        fontFamily={zeutecConfig.fontFamily}
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'inherit'
                        }}
                    >
                        {t(item.title)}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography
                            fontFamily={zeutecConfig.fontFamily}
                            variant="body1"
                            sx={{
                                color: 'inherit',
                                fontWeight: 500
                            }}
                        >
                            {t(item.caption)}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
